import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PANELS,
  MOBILE_VIDEO_SRC,
  POSTER_SRC,
  VIDEO_WIDTH,
  VIDEO_HEIGHT,
  FABRIC_D,
  FABRIC_DT,
} from "./panelData";
import { useLenis } from "@/components/SmoothScrollProvider";

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

// Keyframe strip: 360 frames, 30fps, 12.000s. Frame k (1-based) shows the
// footage at t = (k-1)/30. Desktop tier is 1926w (native master), phone tier 1152w.
const FRAME_COUNT = 360;
const FRAMES_BASE = "/hero-frames";
const pad4 = (n) => String(n).padStart(4, "0");

// Tone of the sheet on screen at fabric time t, for the adaptive nav contrast:
// the calico (t < 4.6) and mist (6.6 <= t < 8.2) sheets are light, rest dark.
const heroToneAt = (t) => (t < 4.6 || (t >= 6.6 && t < 8.2) ? "light" : "dark");

// Mobile-only vertical drop for the copy (px). The copy block is flex-centred in
// the viewport while the roll's reveal clip sits ~26% down; the headline-to-nip
// gap is ~0.24*vh - blockOffset, so it shrinks on short/zoomed phone viewports
// (e.g. iOS Display Zoom renders a phone at 320 CSS px) until the centred
// headline rides UP past the roll's nip and its top is shaved off. The shave is a
// near-constant PIXEL crowd (worst where the viewport is short), so a flat pixel
// drop - not a vh-scaled one - lands the headline just below the nip, on the
// cloth, at every phone size. Applied to every panel so all headlines stay glued
// to their own swatch, only shifted down as one. Desktop keeps its exact
// composition (tier "d" -> 0).
const MOBILE_TEXT_DROP = 40;

/**
 * Cumulative fabric displacement (fractions of the video frame height) at
 * video time t, linearly interpolated from the measured FABRIC_D keyframes.
 */
function fabricD(t) {
  const i = clamp(t / FABRIC_DT, 0, FABRIC_D.length - 1);
  const i0 = Math.floor(i);
  const i1 = Math.min(i0 + 1, FABRIC_D.length - 1);
  return FABRIC_D[i0] + (FABRIC_D[i1] - FABRIC_D[i0]) * (i - i0);
}

// anchorT / wipe.t0 are constants, so their displacements are too - precompute
// once instead of re-interpolating them on every presented frame.
const PANEL_D = PANELS.map((p) => ({
  anchorD: fabricD(p.anchorT),
  wipeD: p.wipe ? fabricD(p.wipe.t0) : 0,
}));

function getMode() {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduced) return "reduced";
  // Phones and tablets now scroll-scrub too (using the lighter 1152w frame
  // set) - only prefers-reduced-motion opts out into the stacked fallback.
  return "scrub";
}

/* ---------- shared panel copy block ---------- */

function PanelCopy({ panel, asHeading, onExplore }) {
  const dark = panel.text === "charcoal";
  const navigate = useNavigate();
  const HeadTag = asHeading ? "h1" : "h2";
  const onCta = () => {
    if (!panel.cta) return;
    if (panel.cta.type === "route") navigate(panel.cta.to);
    else onExplore(panel.cta.to);
  };
  return (
    <div className="relative z-10 flex max-w-3xl flex-col items-center px-6 text-center">
      {/* soft light halo rides with charcoal text so it stays crisp over the cloth */}
      {dark && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -inset-x-24 -inset-y-16"
          style={{
            background:
              "radial-gradient(ellipse 55% 55% at 50% 50%, rgba(250,248,245,0.32), rgba(250,248,245,0) 72%)",
          }}
        />
      )}
      <p
        className={`eyebrow relative mb-5 text-[13px] ${dark ? "text-charcoal/80" : "text-warmwhite/80"}`}
      >
        {panel.eyebrow}
      </p>
      <HeadTag
        className={`font-display relative font-extrabold leading-[1.04] ${
          dark ? "text-charcoal" : "text-warmwhite"
        }`}
        style={{ fontSize: "clamp(2rem, 4.6vw, 3.9rem)" }}
      >
        {panel.headline}
      </HeadTag>
      <p
        className={`relative mt-6 max-w-xl font-inter text-[17px] leading-relaxed ${
          dark ? "text-charcoal/80" : "text-warmwhite/85"
        }`}
      >
        {panel.line}
      </p>
      {panel.cta && (
        <button
          type="button"
          data-testid="hero-cta-explore"
          onClick={onCta}
          className={`eyebrow relative mt-10 border bg-transparent px-8 py-4 text-[13px] transition-colors duration-300 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kraft ${
            dark
              ? "border-charcoal/60 text-charcoal hover:bg-charcoal hover:text-warmwhite"
              : "border-warmwhite/70 text-warmwhite hover:bg-warmwhite hover:text-charcoal"
          }`}
        >
          {panel.cta.label}
        </button>
      )}
    </div>
  );
}

/* ---------- stacked fallback panels (reduced-motion / mobile) ---------- */

function StackedPanels({ withVideo, onExplore }) {
  return (
    <div className="relative">
      {withVideo && (
        <div aria-hidden="true" className="absolute inset-0 overflow-hidden">
          <video
            ref={(el) => {
              if (!el) return;
              // iOS Safari only autoplays when the `muted` ATTRIBUTE is present -
              // React sets the property but not the attribute, so the hero froze
              // on frame 1 on real iPhones. Force the attributes + kick playback.
              el.defaultMuted = true;
              el.muted = true;
              el.setAttribute("muted", "");
              el.setAttribute("playsinline", "");
              el.setAttribute("webkit-playsinline", "");
              el.play?.().catch(() => {});
            }}
            data-testid="hero-video-mobile"
            className="sticky top-0 h-dvh w-full object-cover"
            src={MOBILE_VIDEO_SRC}
            poster={POSTER_SRC}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          />
        </div>
      )}
      {PANELS.map((panel, i) => {
        const dark = panel.text === "charcoal";
        return (
          <section
            key={panel.id}
            data-testid={`hero-stacked-${panel.id}`}
            className="relative flex min-h-dvh items-center justify-center overflow-hidden"
            style={{
              backgroundColor: withVideo ? undefined : panel.bg,
            }}
          >
            {withVideo && (
              <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{ backgroundColor: panel.bg, opacity: 0.82 }}
              />
            )}
            <PanelCopy panel={panel} asHeading={false} onExplore={onExplore} />
            {panel.cue && (
              <div
                aria-hidden="true"
                className={`absolute bottom-8 left-1/2 h-10 w-px -translate-x-1/2 ${
                  dark ? "bg-charcoal/60" : "bg-warmwhite/60"
                }`}
              />
            )}
          </section>
        );
      })}
    </div>
  );
}

/* ---------- main hero ---------- */

export default function ScrollScrubHero() {
  const [mode, setMode] = useState(() =>
    typeof window === "undefined" ? "scrub" : getMode()
  );
  const lenis = useLenis();

  const trackRef = useRef(null);
  const canvasRef = useRef(null);
  const imagesRef = useRef([]); // imagesRef.current[k] = HTMLImageElement (1-based)
  const loadedRef = useRef([]); // sorted ascending list of loaded frame indices
  const toneRef = useRef(null); // last dispatched hero tone
  const moverRefs = useRef([]);
  const scrimRefs = useRef([]);
  const clipRefs = useRef([]);
  const cueRef = useRef(null);
  const rafRef = useRef(0);

  // Watch for viewport / preference changes so the right structure renders.
  useEffect(() => {
    const queries = [
      window.matchMedia("(prefers-reduced-motion: reduce)"),
      window.matchMedia("(max-width: 768px)"),
      window.matchMedia("(pointer: coarse)"),
    ];
    const update = () => setMode(getMode());
    queries.forEach((q) => q.addEventListener("change", update));
    return () => queries.forEach((q) => q.removeEventListener("change", update));
  }, []);

  // Scrub engine v2 - eased catch-up + sub-frame crossfade (see loop below).
  useEffect(() => {
    if (mode !== "scrub") return undefined;
    const canvas = canvasRef.current;
    const track = trackRef.current;
    if (!canvas || !track) return undefined;

    const ctx = canvas.getContext("2d");

    // Tier chosen once on mount: phones/tablets fetch the lighter 1152w set.
    const tier = window.matchMedia("(max-width: 768px)").matches ? "m" : "d";
    // Same mobile signal gates the copy drop that keeps the headline off the roll.
    const textDrop = tier === "m" ? MOBILE_TEXT_DROP : 0;

    const images = imagesRef.current;
    const loaded = loadedRef.current;
    let cancelled = false;

    // keep the loaded-index list sorted so nearest-neighbour is a binary search
    const insertLoaded = (k) => {
      let lo = 0;
      let hi = loaded.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (loaded[mid] < k) lo = mid + 1;
        else hi = mid;
      }
      loaded.splice(lo, 0, k);
    };

    // nearest already-loaded frame to idx (0 when nothing has landed yet)
    const nearestLoaded = (idx) => {
      const n = loaded.length;
      if (n === 0) return 0;
      if (idx <= loaded[0]) return loaded[0];
      if (idx >= loaded[n - 1]) return loaded[n - 1];
      let lo = 0;
      let hi = n - 1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (loaded[mid] === idx) return idx;
        if (loaded[mid] < idx) lo = mid + 1;
        else hi = mid - 1;
      }
      const below = loaded[hi];
      const above = loaded[lo];
      return idx - below <= above - idx ? below : above;
    };

    // Load in waves so scrubbing works almost immediately: a sparse spread
    // first, then progressively fill the gaps. Each wave starts once the prior
    // one has finished landing, keeping the earliest coverage prioritised.
    const buildWaves = () => {
      const requested = new Array(FRAME_COUNT + 1).fill(false);
      const take = (pred) => {
        const out = [];
        for (let k = 1; k <= FRAME_COUNT; k += 1) {
          if (!requested[k] && pred(k)) {
            requested[k] = true;
            out.push(k);
          }
        }
        return out;
      };
      return [
        take((k) => k === 1 || k % 8 === 0), // wave1: frame 1 + every 8th
        take((k) => k % 4 === 0), // wave2: every 4th
        take((k) => k % 2 === 0), // wave3: every 2nd
        take(() => true), // wave4: all remaining
      ];
    };
    const waves = buildWaves();

    const loadWave = (wi) => {
      if (cancelled || wi >= waves.length) return;
      const indices = waves[wi];
      let pending = indices.length;
      if (pending === 0) {
        loadWave(wi + 1);
        return;
      }
      const done = () => {
        pending -= 1;
        if (pending === 0) loadWave(wi + 1);
      };
      indices.forEach((k) => {
        const img = new Image();
        img.decoding = "async";
        img.onload = () => {
          if (cancelled) return;
          images[k] = img;
          insertLoaded(k);
          done();
        };
        img.onerror = done;
        img.src = `${FRAMES_BASE}/${tier}/${pad4(k)}.webp`;
      });
    };
    loadWave(0);

    // object-cover draw of frame `idx` into the canvas backing store, using the
    // frame's own natural size (scale = max(cw/iw, ch/ih), centred crop).
    const draw = (idx) => {
      const img = images[idx];
      if (!img) return;
      const cw = canvas.width;
      const ch = canvas.height;
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;
      if (!cw || !ch || !iw || !ih) return;
      const scale = Math.max(cw / iw, ch / ih);
      const dw = iw * scale;
      const dh = ih * scale;
      ctx.drawImage(img, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
    };

    // Continuous-time draw: the keyframe below t, with the next keyframe
    // crossfaded on top by the sub-frame fraction. The blend makes cloth
    // motion continuous between the 30fps captures - and because FABRIC_D
    // interpolates linearly between the SAME 1/30s knots, the copy tracks
    // the blended cloth, not a stepped approximation of it.
    // Returns false when the exact pair isn't loaded yet (drew nearest).
    const drawAt = (t) => {
      let f = clamp(t * 30, 0, FRAME_COUNT - 1); // 0-based float index
      // when t sits on a keyframe (at rest, or a motion frame boundary) kill
      // the float dust - round()/30 lands a hair under the integer, which would
      // otherwise floor one frame low and paint a 0.0001 ghost instead of crisp
      const fr = Math.round(f);
      if (Math.abs(f - fr) < 1e-4) f = fr;
      const k0 = 1 + Math.floor(f);
      const frac = f - Math.floor(f);
      const k1 = Math.min(k0 + 1, FRAME_COUNT);
      if (images[k0] && (frac < 0.005 || images[k1])) {
        draw(k0);
        if (frac >= 0.005 && k1 !== k0) {
          ctx.globalAlpha = frac;
          draw(k1);
          ctx.globalAlpha = 1;
        }
        return true;
      }
      const near = nearestLoaded(Math.round(f) + 1);
      if (near) draw(near);
      return false;
    };

    // Placement of every panel is a PURE FUNCTION of the fabric time on screen.
    // Each headline is printed on the middle of its own swatch, so it rides
    // exactly as far as that spot of cloth has travelled from centre - revealed
    // at the roll's nip, gliding down (or back up under the roll) with the feed,
    // and off the top/bottom when its swatch centre is out of view.
    const positionText = (tVid) => {
      const vw = window.innerWidth || 1;
      const vh = window.innerHeight || 1;
      const scale = Math.max(vw / VIDEO_WIDTH, vh / VIDEO_HEIGHT);
      const dispH = VIDEO_HEIGHT * scale;
      const cropTop = (dispH - vh) / 2;
      const dNow = fabricD(tVid);
      for (let i = 0; i < PANELS.length; i += 1) {
        const panel = PANELS[i];
        const mover = moverRefs.current[i];
        if (!mover) continue;
        const scrim = scrimRefs.current[i];
        const clip = clipRefs.current[i];

        // glued position: how far this text's spot of cloth has moved from centre
        // (+ the mobile-only drop so short/zoomed phones don't shave the headline)
        const tyPx = (dNow - PANEL_D[i].anchorD) * dispH + textDrop;

        // reveal line: own roll's nip until the next sheet starts feeding,
        // then the descending hem of the covering sheet
        let clipFrac = panel.nip;
        if (panel.wipe && tVid > panel.wipe.t0) {
          clipFrac = Math.max(clipFrac, panel.wipe.nip + (dNow - PANEL_D[i].wipeD));
        }
        const clipPx = clamp(clipFrac * dispH - cropTop, 0, vh);

        const onScreen = tyPx > -1.9 * vh && tyPx < 1.7 * vh && clipPx < vh - 1;
        if (!onScreen) {
          mover.style.opacity = "0";
          mover.style.visibility = "hidden";
          mover.style.pointerEvents = "none";
          if (scrim) scrim.style.opacity = "0";
          continue;
        }
        if (clip) clip.style.clipPath = `inset(${clipPx.toFixed(1)}px 0 0 0)`;
        mover.style.visibility = "visible";
        // Headline opacity: distance-based ramp keyed to |tyPx| (how far this
        // headline's swatch centre has travelled from viewport centre), built
        // like the scrim fade below. Full (1) only while the swatch centre is
        // within 0.10*vh of centre, then linearly to 0 by 0.30*vh - so each
        // headline is gone BEFORE the next becomes legible. The smallest measured
        // tyPx gap between adjacent panels is 0.629*vh (mist->ember); its
        // crossover sits both headlines at |tyPx| = 0.314*vh, and because the fade
        // completes at 0.30*vh (and 0.629 > 2*0.30) the two never coexist on
        // screen. The wipe factor is multiplied in so a covered headline also
        // fades as the next sheet's descending hem clips it.
        let moverO = clamp(1 - (Math.abs(tyPx) - 0.1 * vh) / (0.2 * vh), 0, 1);
        if (panel.wipe && clipFrac > panel.nip) {
          moverO *= clamp(1 - (clipFrac - panel.wipe.nip) / 0.6, 0, 1);
        }
        mover.style.opacity = moverO.toFixed(3);
        mover.style.transform = `translate3d(0, ${tyPx.toFixed(1)}px, 0)`;
        mover.style.pointerEvents = Math.abs(tyPx) < 0.55 * vh ? "auto" : "none";
        if (scrim) {
          // scrim tracks the text: full strength while readable near centre,
          // gone once the copy has ridden away or been wiped
          let scrimO = clamp(1 - (Math.abs(tyPx) - 0.4 * vh) / (0.35 * vh), 0, 1);
          if (panel.wipe && clipFrac > panel.nip) {
            scrimO *= clamp(1 - (clipFrac - panel.wipe.nip) / 0.6, 0, 1);
          }
          scrim.style.opacity = scrimO.toFixed(3);
        }
      }
    };

    // Broadcast the light/dark tone of the displayed sheet so the nav can flip
    // its contrast (only fires on an actual change).
    const emitTone = (t) => {
      const tone = heroToneAt(t);
      if (tone !== toneRef.current) {
        toneRef.current = tone;
        document.documentElement.dataset.heroTone = tone;
        window.dispatchEvent(new CustomEvent("herotone", { detail: { tone } }));
      }
    };

    // Place the copy + tone once up front, before the first painted frame.
    emitTone(0);
    positionText(0);

    // SCRUB v2: the strict 1:1 snap quantized both footage and copy to whole
    // 30fps frames (~7px of scroll per step), so smooth scrolling produced
    // stepped 10-20px text jumps, and any rAF gap (busy phones, throttled
    // tabs) became an instant teleport. Standard image-sequence practice
    // (GSAP scrub / Apple product pages): ease the DISPLAYED time toward the
    // scroll-mapped target and render continuously from that eased time.
    const T_MAX = (FRAME_COUNT - 1) / 30; // 11.9667s of footage
    const TAU = 0.09; // s - catch-up constant: short enough to feel locked
    const SNAP = 1 / 240; // within a quarter frame: land exactly and rest
    const REST_EPS = 1e-4; // s - target moving under this = scroll is at rest

    let tShown = null; // continuous fabric time currently rendered
    let lastNow = null;
    let lastTarget = null; // previous frame's scroll target - rest = it holds still
    let lastDrawT = -1;
    let lastLoadedCount = -1;

    const loop = (now) => {
      const rect = track.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const total = Math.max(rect.height - vh, 1);
      const p = clamp(-rect.top / total, 0, 1);
      const tTarget = p * T_MAX;

      // Rest = the scroll input has stopped moving. While it keeps changing we
      // chase the continuous target and the sub-frame crossfade shows the cloth
      // mid-motion; the instant it holds still we retarget the eased time onto
      // the NEAREST exact keyframe, so the sheet settles to one crisp, unblended
      // capture (and the copy, driven by the same tShown, lands glued to it).
      // Rest is read from a stationary tTarget - not from tShown's distance to a
      // frame edge - so there is no oscillation when the scroll rests near a
      // boundary; REST_EPS sits far below one pixel of scroll travel.
      const atRest =
        lastTarget !== null && Math.abs(tTarget - lastTarget) < REST_EPS;
      lastTarget = tTarget;
      const goal = atRest ? Math.round(tTarget * 30) / 30 : tTarget;

      // frame-rate-independent eased catch-up; dt is clamped so a long rAF
      // gap glides over the next few frames instead of teleporting
      if (tShown === null || lastNow === null) {
        tShown = goal;
      } else {
        const dt = Math.min((now - lastNow) / 1000, 0.1);
        tShown += (goal - tShown) * (1 - Math.exp(-dt / TAU));
        if (Math.abs(goal - tShown) < SNAP) tShown = goal;
      }
      lastNow = now;

      // keep the canvas backing store matched to its CSS box (dpr capped at 2)
      let sizeChanged = false;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const bw = Math.round(canvas.clientWidth * dpr);
      const bh = Math.round(canvas.clientHeight * dpr);
      if (bw > 0 && bh > 0 && (canvas.width !== bw || canvas.height !== bh)) {
        canvas.width = bw;
        canvas.height = bh;
        sizeChanged = true;
      }

      // redraw only when the shown time, canvas size, or loaded set changed;
      // an approximate draw (pair not loaded yet) retries every frame
      if (
        sizeChanged ||
        tShown !== lastDrawT ||
        loaded.length !== lastLoadedCount
      ) {
        const exact = drawAt(tShown);
        lastDrawT = exact ? tShown : -1;
        lastLoadedCount = loaded.length;
      }

      // copy + tone from the SAME continuous time as the canvas, so cloth
      // and text stay welded by construction.
      positionText(tShown);
      emitTone(tShown);

      // Scroll cue stays pinned at the bottom of the first view, fades on scroll.
      if (cueRef.current) {
        cueRef.current.style.opacity = String(clamp(1 - p / 0.04, 0, 1));
      }

      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.current);
      imagesRef.current = [];
      loadedRef.current = [];
    };
  }, [mode]);

  const handleExplore = (targetId) => {
    const target = document.getElementById(targetId || "warehouse");
    if (!target) return;
    if (lenis) lenis.scrollTo(target, { duration: 1.4 });
    else target.scrollIntoView({ behavior: "smooth" });
  };

  /* ----- reduced motion: poster + stacked colourway sections ----- */
  if (mode === "reduced") {
    return (
      <header id="hero" data-testid="hero-reduced" className="bg-ink">
        <img
          src={POSTER_SRC}
          alt="A bolt of calico cream performance fabric, unrolled"
          className="h-dvh w-full object-cover"
        />
        <StackedPanels withVideo={false} onExplore={handleExplore} />
      </header>
    );
  }


  /* ----- desktop/mobile: scroll-scrubbed sticky canvas, copy riding the cloth ----- */
  return (
    <header
      id="hero"
      ref={trackRef}
      data-testid="hero-track"
      className="relative bg-ink"
      style={{ height: "400vh" }}
    >
      <div className="sticky top-0 h-dvh w-full overflow-hidden bg-ink">
        {/* poster underlay for instant first paint - the canvas covers it once
            the first keyframe lands */}
        <img
          src={POSTER_SRC}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <canvas
          ref={canvasRef}
          data-testid="hero-canvas"
          aria-hidden="true"
          className="absolute inset-0 h-full w-full"
        />

        {PANELS.map((panel, i) => (
          <div
            key={panel.id}
            data-testid={`hero-panel-${panel.id}`}
            className="pointer-events-none absolute inset-0"
          >
            {/* legibility scrim for light-text panels only; charcoal panels use the halo */}
            {panel.text === "warm" && (
              <div
                ref={(el) => {
                  scrimRefs.current[i] = el;
                }}
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                  opacity: 0,
                  background:
                    "linear-gradient(to bottom, rgba(0,0,0,0.15), rgba(0,0,0,0.35))",
                }}
              />
            )}
            {/* static clip plane: hides anything above the roll's nip line so the
                copy is revealed exactly where the cloth feeds off the roll */}
            <div
              ref={(el) => {
                clipRefs.current[i] = el;
              }}
              data-testid={`hero-clip-${panel.id}`}
              className="absolute inset-0"
            >
              {/* the mover: rides down the viewport in sync with the fabric feed */}
              <div
                ref={(el) => {
                  moverRefs.current[i] = el;
                }}
                data-testid={`hero-mover-${panel.id}`}
                className="absolute inset-0 flex items-center justify-center will-change-transform"
                style={{
                  opacity: i === 0 ? 1 : 0,
                  visibility: i === 0 ? "visible" : "hidden",
                  pointerEvents: i === 0 ? "auto" : "none",
                }}
              >
                <PanelCopy panel={panel} asHeading={false} onExplore={handleExplore} />
              </div>
            </div>
          </div>
        ))}

        {/* pulsing scroll cue - pinned to the bottom of the first view */}
        <div
          ref={cueRef}
          aria-hidden="true"
          data-testid="hero-scroll-cue"
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="scroll-cue h-10 w-px bg-charcoal/70" />
        </div>
      </div>
    </header>
  );
}
